import { ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { NoResultFoundComponent } from 'app/modules/shared/components/no-result-found/no-result-found.component';
import { AccessProviderDirective } from 'app/modules/shared/directives/access-provider.directive';
import { StorageService } from 'app/modules/shared/services/storage.service';
import { MenuItem } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { MenuModule } from 'primeng/menu';
import { TabViewModule } from 'primeng/tabview';
import { ToastModule } from 'primeng/toast';
import { AppSettings } from '../../../shared/app.settings';
import { StatusComponent } from '../../../shared/components/status/status.component';
import type { Country } from '../../../shared/models/country';
import type { Status } from '../../../shared/models/status';
import { CommonBindingDataService } from '../../../shared/services/common-binding-data.service';
import { EntityService } from '../../../shared/services/entity.service';
import { ProductOverviewDetailsComponent } from './overview-details/overview-details.component';
import { PermissionAccessService } from 'app/modules/shared/services/permission-access.service';
import { DatePipe } from '@angular/common';
import { MetaDataService } from 'app/modules/shared/services/meta-data.service';

@Component({
  selector: 'app-product-details',
  standalone: true,
  imports: [
    BreadcrumbModule,
    StatusComponent,
    TabViewModule,
    TranslateModule,
    MenuModule,
    ToastModule,
    ProductOverviewDetailsComponent,
    ButtonModule,
    AccessProviderDirective,
    NoResultFoundComponent,
  ],
  templateUrl: './product-details.component.html',
  styleUrl: './product-details.component.scss'
})

export class ProductDetailsComponent implements OnInit {
  @Input() tableData: any[];
  routePath: MenuItem[] = [];
  productId: string;
  dataForVehicleStatus: Array<Status> = [];
  userId: any = '';
  country: Country;
  activeIndex: any = 0;
  data: any;
  productAttributeData: any;
  attributeLabels = {};
  productAttributeLength;
  attributeValues = [];
  imgFileId: any;
  imgFileUrl: any;
  showStatus: boolean = false;
  productEntityId;
  currentRow;
  productDetails: any;
  entityForStatus: any;
  editBtnLabel: any;
  relatedData: any;
  driverList: any[] = [];
  defaultLoaderImg = AppSettings.DEFAULT_LOADER_IMAGE;
  entityCode: string = AppSettings.ENTITY_CODE.VEHICLE_DOCUMENT;
  isUnassigned: boolean = false;
  isDeleted: boolean = false;
  isDeletedProduct: boolean = false;
  datePipe: DatePipe;
  packTypeValue: any;
  storageValue: any;

  constructor(private route: ActivatedRoute,
    public cs: CommonBindingDataService,
    private cd: ChangeDetectorRef,
    private productService: EntityService,
    private storageService: StorageService,
    private router: Router,
    private metaDataService: MetaDataService,
    private permissionAccessService: PermissionAccessService) {
    this.datePipe = new DatePipe('en-US');
  }

  ngOnInit() {
    this.editBtnLabel = this.cs.getLabel('lbl_edit');
    this.productId = this.route.snapshot.paramMap.get('id');
    this.isDeletedProduct = +this.storageService.getItem('productActiveIndex') === 2;
    this.getAttributeData();
    this.routePath = [
      {
        label: this.cs.getLabel('product.lbl_product'),
        routerLink: '/app/products',
        icon: 'pi pi-arrow-left',
        iconStyle: { 'font-weight': 'bold', 'margin-right': '10px' },
        command: () => {
          const fromTab = this.route.snapshot.queryParams.fromTab;
          if (fromTab) {
            this.router.navigate(['/app/products'], { queryParams: { tab: fromTab } });
          } else {
            this.router.navigate(['/app/products']);
          }
        }
      },
      {
        label: this.cs.getLabel('product.lbl_overview'),
        routerLink: `../${this.productId}`,
        styleClass: 'breadcrumb-child forward-slash breadcrumb-text',
        style: { 'display': 'flex', 'top': '2px', 'position': 'relative' }
      },
    ];
    this.productService.entityCurrentRow.subscribe(res => {
      this.currentRow = res;
    });


    this.isDeleted = this.permissionAccessService.hasAccess('PRODUCTS', '', 'ALL', 'DELETE');

  }

  activeIndexChange(event) {
    this.storageService.setItem('vehicleActiveIndex', event);
  }

  getAttributeData() {
    this.country = JSON.parse(localStorage.getItem(AppSettings.COUNTRY));
    this.productService.getAttributeDefinition(AppSettings.ENTITY_CODE.PRODUCT, AppSettings.VIEW_CODE.DETAIL_VIEW).subscribe(res => {
      this.data = res;
      this.attributeLabels = this.cs.getAttributeLabels(this.data);
      this.productAttributeData = this.cs.getOrganizedAttribute(this.data);
      this.productAttributeLength = this.productAttributeData.tabs.length;
      this.getProductDetails();
    })
  }

  getProductDetails() {
    this.productService.getEntity(this.productId, AppSettings.ENTITY_CODE.PRODUCT, AppSettings.VIEW_CODE.DETAIL_VIEW).subscribe((result: any) => {
      const packTypeItem = result.attributeCodeValueDtoList.find(item => item.attributeCode === 'pack_type');

      if (packTypeItem && typeof packTypeItem.attributeValue === 'object') {
        const values = Object.values(packTypeItem.attributeValue);
        this.packTypeValue = values.length > 0 ? values[0] : undefined;
      }

      const storageItem = result.attributeCodeValueDtoList.find(item => item.attributeCode === 'storage');

      if (storageItem && typeof storageItem.attributeValue === 'object') {
        const values = Object.values(storageItem.attributeValue);
        this.storageValue = values.length > 0 ? values[0] : undefined;
      }

      this.attributeValues = this.cs.getOrgAttributeValues(result);
      this.productDetails = result;
      this.showStatus = true;
      this.setStatusData();
    });
  }

  setStatusData() {
    const findAttributeValue = (attributeCode: string) =>
      this.productDetails?.attributeCodeValueDtoList.find(ele => ele.attributeCode === attributeCode)?.attributeValue || this.cs.getLabel('lbl_empty');

    const getAttributeValueOrEmpty = (attributeCode: string) =>
      findAttributeValue(attributeCode) || this.cs.getLabel('lbl_empty');
    const statusObj = findAttributeValue('product_status');

    this.entityForStatus = {
      entityName: `${findAttributeValue('product_name')}`,
      entityStatus: statusObj
    };

    this.dataForVehicleStatus = [
      { text: getAttributeValueOrEmpty('product_code'), vehicleType: 'type' },
      { label: '', vehicleType: 'type', text: (getAttributeValueOrEmpty('entity_created_at') ? this.metaDataService.formatDateTimeOnTZ(getAttributeValueOrEmpty('entity_created_at'), AppSettings.DD_MMM_YYYY_hh_mm_FORMAT2) : '') },
    ];
  }

  edit() {
    this.router.navigate([`app/products/add-product/${this.productDetails.entityId}`,]);
  }

  getDeleted(): boolean {
    return this.productDetails?.actionStatus === 'DELETED';
  }

  getUnassigned(): boolean {
    return this.productDetails?.actionStatus === 'UNASSIGNED';
  }

}
